import { Type } from "@themes/pollinator/ui/Type";
import { marketingAccent, marketingType } from "../../marketing.type";

export type Props = {
  /** The small underlined label above the heading */
  eyebrow?: string;
  /** A node rather than a string, because the design colours words inside it */
  heading: React.ReactNode;
  /** Paragraphs under the heading */
  body?: string[];
  /**
   * Larger heading, for the statements that open a part of the page. Both sizes
   * are smaller than they look in Figma at first glance: the design's canvas is
   * 1440 wide, so a heading that fills it is not a heading that should fill a
   * laptop.
   */
  size?: "statement" | "section";
  children?: React.ReactNode;
  className?: string;
  /** An address for the claim itself, where a link names it rather than its band */
  id?: string;
};

/**
 * The way every section announces itself: a label, a claim, then the argument.
 *
 * The label is underlined in the accent green, and the claim runs the full
 * width of the band, which is what lets it break where the design breaks it.
 */
export function MarketingSectionIntro({
  eyebrow,
  heading,
  body,
  size = "section",
  children,
  className,
  id,
}: Props) {
  return (
    <div
      id={id}
      className={`flex flex-col gap-4 w-full ${className ?? ""}`}
      data-testid="section-intro"
    >
      {eyebrow && (
        <Type
          variant="caption"
          as="p"
          className={`${marketingType.eyebrow} ${marketingAccent} self-start max-w-none`}
        >
          {eyebrow}
        </Type>
      )}
      <Type
        variant={size === "statement" ? "h3" : "h4"}
        as="h2"
        className={
          size === "statement"
            ? `${marketingType.display} font-bold`
            : undefined
        }
      >
        {heading}
      </Type>
      {(body ?? []).map((paragraph) => (
        <Type
          variant="body1"
          key={paragraph}
          className={`${marketingType.body} max-w-none lg:pt-4`}
        >
          {paragraph}
        </Type>
      ))}
      {children}
    </div>
  );
}

export default MarketingSectionIntro;
